import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getReservationsByDate, getReservationsByDateRange, Reservation, generatePublicCheckinForm, getAttentionRequiredReservations } from '../api.reservations';
import { createReservation } from '../api.reservations';
import { getApartments, Apartment } from '../api.apartments';
import { getPendingStayExtensionCount } from '../api.stayExtension';
import DepositQueue from '../components/DepositQueue';
import { stayExtensionText } from '../i18n/pages/stayExtension';
import { getUbyportAvailability, UbyportAvailabilityStatus } from '../api.ubyport';
import PlusIcon from '../components/PlusIcon';
import EditIcon from '../components/icons/EditIcon';
import BookIcon from '../components/icons/BookIcon';
import PoliceIcon from '../components/icons/PoliceIcon';
import CityTaxIcon from '../components/icons/CityTaxIcon';
import EyeIcon from '../components/icons/EyeIcon';
import KeyIcon from '../components/icons/KeyIcon';
import Pagination from '../components/Pagination';
import ReservationItem from '../components/ReservationItem';
import useIsMobile from '../hooks/useIsMobile';
import { colors, ACTION_BTN_H, ICON_BTN_SIZE, ACTION_BTN_RADIUS } from '../theme';
import { getTtlockBadgeInfo, getGuideBadgeInfo } from '../utils/reservationBadges';
import { formatStayRange } from '../utils/stayDates';
import StayHours from '../components/StayHours';
import LuggageStatusIcon from '../components/LuggageStatusIcon';
import { useLang } from '../i18n/LanguageContext';
import { homeText } from '../i18n/pages/home';
import { common } from '../i18n/pages/common';

const getToken = () => localStorage.getItem('token') || '';

const dateAddDays = (iso: string, days: number): string => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const VIEW_MODE_STORAGE_KEY = 'home.viewMode';
const SHOW_ONGOING_STORAGE_KEY = 'home.showOngoingSection';
const SHOW_INCOMING_STORAGE_KEY = 'home.showIncomingSection';
const PAGE_SIZE_STORAGE_KEY = 'home.pageSize';
const PAGE_SIZE_OPTIONS = [10, 25, 50];

type ViewMode = '1day' | '3day' | '5day';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const t = homeText[lang];
  const tse = stayExtensionText[lang];
  const [pendingExtensions, setPendingExtensions] = useState(0);
  const tc = common[lang];
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return (saved === '1day' || saved === '3day' || saved === '5day') ? saved : '1day';
  });
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [attentionReservations, setAttentionReservations] = useState<Reservation[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<Reservation>({
    apartmentId: 0,
    guestName: '',
    checkInDate: '',
    checkOutDate: '',
    numberOfGuests: 1,
    notes: '',
  });
  const [showOngoing, setShowOngoing] = useState(() => localStorage.getItem(SHOW_ONGOING_STORAGE_KEY) !== 'false');
  const [showIncoming, setShowIncoming] = useState(() => localStorage.getItem(SHOW_INCOMING_STORAGE_KEY) !== 'false');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = parseInt(localStorage.getItem(PAGE_SIZE_STORAGE_KEY) || '', 10);
    return PAGE_SIZE_OPTIONS.includes(saved) ? saved : 10;
  });
  const [ubyportStatus, setUbyportStatus] = useState<UbyportAvailabilityStatus | null>(null);
  const isMobile = useIsMobile();

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseLocalDate = (value: string): Date => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, (month || 1) - 1, day || 1);
  };

  const fetchReservations = async () => {
    setLoading(true);
    setError(null);
    try {
      let data: Reservation[];
      if (viewMode === '1day') {
        data = await getReservationsByDate(formatDate(currentDate), getToken());
      } else {
        const days = viewMode === '3day' ? 3 : 5;
        const endDate = new Date(currentDate);
        endDate.setDate(currentDate.getDate() + days - 1);
        data = await getReservationsByDateRange(formatDate(currentDate), formatDate(endDate), getToken());
      }
      setReservations(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load reservations');
    } finally {
      setLoading(false);
    }
  };

  const fetchApartments = async () => {
    try {
      const data = await getApartments(getToken());
      setApartments(data);
    } catch (err: any) {
      console.error('Failed to load apartments:', err);
    }
  };

  const fetchAttentionReservations = async () => {
    try {
      const data = await getAttentionRequiredReservations(getToken());
      setAttentionReservations(data);
    } catch (err) {
      console.error('Failed to load attention reservations:', err);
    }
  };

  const fetchUbyportStatus = async () => {
    try {
      const status = await getUbyportAvailability();
      setUbyportStatus(status);
    } catch {
      // silently ignore — availability banner is non-critical
    }
  };

  useEffect(() => {
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) {
      fetchReservations();
      fetchApartments();
      fetchAttentionReservations();
      fetchUbyportStatus();
      // Same count the nav badge shows. Fetched here too rather than lifted into shared state:
      // it is one cheap call, and a stale number on the dashboard would send someone to an empty
      // queue — worse than fetching it twice.
      getPendingStayExtensionCount().then(setPendingExtensions).catch(() => setPendingExtensions(0));
    }
  }, [currentDate, viewMode]);

  useEffect(() => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem(SHOW_ONGOING_STORAGE_KEY, showOngoing ? 'true' : 'false');
  }, [showOngoing]);

  useEffect(() => {
    localStorage.setItem(SHOW_INCOMING_STORAGE_KEY, showIncoming ? 'true' : 'false');
  }, [showIncoming]);

  useEffect(() => {
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
  }, [pageSize]);

  const sortedReservations = [...reservations].sort((a, b) => {
    const dateCompare = a.checkInDate.localeCompare(b.checkInDate);
    if (dateCompare !== 0) return dateCompare;
    return a.checkOutDate.localeCompare(b.checkOutDate);
  });
  const totalPages = Math.ceil(sortedReservations.length / pageSize);
  const pagedReservations = sortedReservations.slice(page * pageSize, (page + 1) * pageSize);
  // Relative to the day currently being browsed (Previous/Next), not the real calendar date —
  // otherwise a reservation whose stay is entirely in the past relative to *today* falls into
  // neither bucket and vanishes from the page even when you navigate back to its own dates.
  const viewDateIso = formatDate(currentDate);

  const isOngoingReservation = (reservation: Reservation) =>
    reservation.checkInDate < viewDateIso && reservation.checkOutDate > viewDateIso;

  const isIncomingReservation = (reservation: Reservation) =>
    reservation.checkInDate >= viewDateIso;

  const ongoingReservations = pagedReservations.filter(isOngoingReservation);
  const incomingReservations = pagedReservations.filter(isIncomingReservation);

  useEffect(() => {
    setPage(0);
  }, [reservations.length, viewMode, pageSize]);

  const viewDays = viewMode === '1day' ? 1 : viewMode === '3day' ? 3 : 5;

  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - viewDays);
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + viewDays);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentDate(parseLocalDate(e.target.value));
  };

  const handleAdd = () => {
    setForm({
      apartmentId: apartments.length > 0 ? apartments[0].id! : 0,
      guestName: '',
      checkInDate: formatDate(currentDate),
      checkOutDate: formatDate(new Date(currentDate.getTime() + 86400000)),
      numberOfGuests: 1,
      notes: '',
    });
    setShowForm(true);
    setSuccess(null);
    setError(null);
  };

  const handleEdit = (reservation: Reservation) => {
    if (reservation.id) navigate(`/reservations/${reservation.id}?back=/`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createReservation(form, getToken());
      setSuccess(t.reservationCreated);
      setShowForm(false);
      fetchReservations();
      fetchAttentionReservations();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Save failed');
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(f => {
      const updated = {
        ...f,
        [name]: name === 'apartmentId' || name === 'numberOfGuests' ? (value === '' ? null : parseInt(value)) : value,
      };
      if (name === 'checkInDate' && value && updated.checkOutDate && updated.checkOutDate <= value) {
        updated.checkOutDate = dateAddDays(value, 1);
      }
      if (name === 'checkOutDate' && value && updated.checkInDate && updated.checkInDate >= value) {
        updated.checkInDate = dateAddDays(value, -1);
      }
      return updated;
    });
  };

  const getDateRangeDisplay = () => {
    if (viewMode === '1day') {
      return currentDate.toLocaleDateString(t.dateLocale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    } else {
      const endDate = new Date(currentDate);
      endDate.setDate(currentDate.getDate() + viewDays - 1);
      return `${currentDate.toLocaleDateString(t.dateLocale, { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString(t.dateLocale, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
  };

  const getGuestbookStatusValue = (status?: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'INCOMPLETE':
      case 'DEPRECATED':
      case 'NOT_FILLED':
        return status;
      default:
        return 'NOT_FILLED';
    }
  };

  const getUbyportStatusValue = (status?: string) => {
    switch (status) {
      case 'SENT':
      case 'SENDING':
      case 'SEND_FAILED':
      case 'SEND_UNAVAILABLE':
      case 'PARTIAL':
      case 'NOT_SENT':
        return status;
      default:
        return 'NOT_SENT';
    }
  };

  const getGuestbookStatusBadge = (status?: string) => {
    const value = getGuestbookStatusValue(status);
    switch (value) {
      case 'COMPLETED':
        return { label: t.statusCompleted, bg: '#c6f6d5', color: '#22543d' };
      case 'INCOMPLETE':
        return { label: t.statusIncomplete, bg: '#feebc8', color: '#7b341e' };
      case 'DEPRECATED':
        return { label: t.statusDeprecated, bg: '#e2e8f0', color: '#4a5568' };
      case 'NOT_FILLED':
      default:
        return { label: t.statusNotFilled, bg: '#fed7d7', color: '#742a2a' };
    }
  };

  const getUbyportStatusBadge = (status?: string) => {
    const value = getUbyportStatusValue(status);
    switch (value) {
      case 'SENT':
        return { label: t.statusSent, bg: '#c6f6d5', color: '#22543d' };
      case 'SENDING':
        return { label: t.statusSending, bg: '#feebc8', color: '#7b341e' };
      case 'SEND_FAILED':
        return { label: t.statusFailed, bg: '#fed7d7', color: '#742a2a' };
      case 'SEND_UNAVAILABLE':
        return { label: t.statusUnavailable, bg: '#feebc8', color: '#7b341e' };
      case 'PARTIAL':
        return { label: t.statusPartial, bg: '#fed7d7', color: '#742a2a' };
      case 'NOT_SENT':
      default:
        return { label: t.statusNotSent, bg: '#e2e8f0', color: '#2d3748' };
    }
  };

  const getCityTaxStatusBadge = (status?: string) => {
    return status === 'PAID'
      ? { label: t.cityTaxPaidLabel, bg: '#c6f6d5', color: '#22543d' }
      : { label: t.cityTaxUnpaidLabel, bg: '#fed7d7', color: '#742a2a' };
  };

  const userEmail = localStorage.getItem('userEmail');

  if (!userEmail) {
    return (
      <div className="page-container" style={{ maxWidth: 900, textAlign: 'center' }}>
        <h1>{t.welcomeTitle}</h1>
        <p><a href="/login">{t.welcomeLogin}</a></p>
      </div>
    );
  }

  const handleOpenApartment = (apartmentId?: number) => {
    if (!apartmentId) return;
    navigate(`/apartments/${apartmentId}`);
  };

  const getPublicFormAbsoluteUrl = (reservation: Reservation) => {
    if (reservation.publicCheckinFormUrl) {
      return `${window.location.origin}${reservation.publicCheckinFormUrl}`;
    }
    if (reservation.publicCheckinFormToken) {
      return `${window.location.origin}/check-in/form/${reservation.publicCheckinFormToken}`;
    }
    return null;
  };

  const handleOpenGuestForm = async (reservation: Reservation) => {
    if (!reservation.id) return;
    if (reservation.status === 'OVERBOOKED') {
      setError(t.errorInactiveForm);
      return;
    }

    let current = reservation;
    let formUrl = getPublicFormAbsoluteUrl(current);

    if (!formUrl) {
      try {
        const updated = await generatePublicCheckinForm(reservation.id, getToken());
        current = { ...reservation, ...updated };
        setReservations((prev) => prev.map((r) => (r.id === reservation.id ? { ...r, ...updated } : r)));
        formUrl = getPublicFormAbsoluteUrl(current);
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to generate guest form URL');
        return;
      }
    }

    if (!formUrl) {
      setError(t.errorNoFormUrl);
      return;
    }

    window.open(formUrl, '_blank', 'noopener,noreferrer');
  };

  const renderReservations = (items: Reservation[]) => {
    if (items.length === 0) return null;

    if (isMobile) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((reservation) => {
            const guestbookStatus = getGuestbookStatusBadge(reservation.guestbookStatus);
            const ubyportStatus = getUbyportStatusBadge(reservation.ubyportStatus);
            const cityTaxStatus = reservation.cityTaxCollectionEnabled ? getCityTaxStatusBadge(reservation.cityTaxStatus) : null;
            return (
              <ReservationItem
                key={reservation.id}
                reservation={reservation}
                layout='compact'
                apartmentText={reservation.apartmentName || `Apartment #${reservation.apartmentId}`}
                guestbookStatus={guestbookStatus}
                ubyportStatus={ubyportStatus}
                cityTaxStatus={cityTaxStatus}
                onOpenGuestForm={handleOpenGuestForm}
                onEdit={handleEdit}
              />
            );
          })}
        </div>
      );
    }

    const iconBadgeStyle = (bg: string): React.CSSProperties => ({
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 24, height: 24, borderRadius: 999, background: bg, flexShrink: 0,
    });

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableGuest}</th>
              <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableApartment}</th>
              <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableDates}</th>
              <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableGuests}</th>
              <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableStatus}</th>
              <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableActions}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((reservation) => {
              const guestbook = getGuestbookStatusBadge(reservation.guestbookStatus);
              const uby = getUbyportStatusBadge(reservation.ubyportStatus);
              const cityTax = reservation.cityTaxCollectionEnabled ? getCityTaxStatusBadge(reservation.cityTaxStatus) : null;
              const ttlock = getTtlockBadgeInfo(reservation);
              const guide = getGuideBadgeInfo(reservation);
              const isOverbooked = reservation.status === 'OVERBOOKED';
              const hasManageLink = !!(reservation.id && reservation.publicCheckinFormToken);
              // Day and month, plus the year only when the stay leaves the current one.
              const stay = formatStayRange(reservation.checkInDate, reservation.checkOutDate);
              return (
                <tr key={reservation.id} style={{ borderBottom: '1px solid #e2e8f0', background: isOverbooked ? '#fff5f5' : undefined }}>
                  <td style={{ padding: 12 }}>
                    <div style={{ fontWeight: 600 }}>{reservation.guestName}</div>
                    {reservation.status === 'CANCELLED' && (
                      <span style={{ display: 'inline-block', marginTop: 2, padding: '1px 7px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700, background: '#fed7d7', color: '#9b2c2c' }}>
                        {t.cancelledLabel}
                      </span>
                    )}
                    {isOverbooked && (
                      <div style={{ fontSize: '0.8rem', color: '#c53030', marginTop: 4 }}>
                        {t.inactiveConflict}
                      </div>
                    )}
                    {reservation.notes && (
                      <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: 2 }}>
                        {reservation.notes.substring(0, 50)}{reservation.notes.length > 50 ? '...' : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 12 }}>{reservation.apartmentName || `Apartment #${reservation.apartmentId}`}</td>
                  {/* Vertical padding trimmed so the hours line costs about four pixels of row
                      height rather than half a row. Only this cell changes. */}
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                    <div style={{ lineHeight: 1.3 }}>{stay.from} → {stay.to}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                      <StayHours checkin={reservation.checkinHours} checkout={reservation.checkoutHours} />
                      <LuggageStatusIcon status={reservation.luggageStatus} />
                    </div>
                  </td>
                  <td style={{ padding: 12 }}>{reservation.numberOfGuests ?? '?'}</td>
                  <td style={{ padding: 12 }}>
                    {/* Guestbook + Ubyport are always rendered (state varies by colour) and stay first,
                        in that fixed order, so the eye scans the same two positions every row.
                        Smart-lock + Guide-opened are optional and only ever append after them —
                        never inserted ahead of the fixed pair, or it would drift row to row. */}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span title={`Guestbook: ${guestbook.label}`} style={iconBadgeStyle(guestbook.bg)}>
                        <BookIcon size={12} color={guestbook.color} />
                      </span>
                      <span title={`Ubyport: ${uby.label}`} style={iconBadgeStyle(uby.bg)}>
                        <PoliceIcon size={12} color={uby.color} />
                      </span>
                      {cityTax && (
                        <span title={`Místní poplatek: ${cityTax.label}`} style={iconBadgeStyle(cityTax.bg)}>
                          <CityTaxIcon size={12} color={cityTax.color} />
                        </span>
                      )}
                      {ttlock && (
                        <span title={ttlock.tooltip} style={iconBadgeStyle(ttlock.bg)}>
                          <KeyIcon size={12} color={ttlock.color} />
                        </span>
                      )}
                      {guide && (
                        <span title={guide.tooltip} style={iconBadgeStyle(guide.bg)}>
                          <EyeIcon size={12} color={guide.color} />
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: 12 }}>
                    {/* Same fixed-then-optional rule as the status icons: Edit + Form always exist
                        (Form just toggles enabled/disabled), Manage is optional and appends after —
                        never pushed in front, so Edit/Form stay in a consistent place. */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button onClick={() => handleEdit(reservation)} title="Edit" style={{ width: ICON_BTN_SIZE, height: ICON_BTN_SIZE, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: colors.brand, color: '#fff', border: 'none', borderRadius: ACTION_BTN_RADIUS, cursor: 'pointer' }}>
                        <EditIcon size={15} />
                      </button>
                      <button
                        onClick={() => handleOpenGuestForm(reservation)}
                        disabled={isOverbooked}
                        style={{
                          height: ACTION_BTN_H,
                          padding: '0 12px',
                          background: isOverbooked ? '#a0aec0' : colors.brand,
                          color: '#fff',
                          border: 'none',
                          borderRadius: ACTION_BTN_RADIUS,
                          cursor: isOverbooked ? 'not-allowed' : 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isOverbooked ? t.inactiveLabel : t.form}
                      </button>
                      {hasManageLink && (
                        <Link
                          to={`/reservations/${reservation.id}/checkin-form`}
                          style={{ display: 'inline-flex', alignItems: 'center', height: ACTION_BTN_H, padding: '0 12px', background: '#edf2f7', color: '#2d3748', borderRadius: ACTION_BTN_RADIUS, fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
                        >
                          {t.manage}
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderReservationSection = (title: string, items: Reservation[]) => {
    if (items.length === 0) return null;
    return (
      <section style={{ marginTop: 24 }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: 700, color: '#2d3748' }}>
          {title} ({items.length})
        </h4>
        {renderReservations(items)}
      </section>
    );
  };

  const dedupedAttentionReservations = attentionReservations.filter((reservation, index, list) =>
    list.findIndex((candidate) => candidate.id === reservation.id) === index
  );

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#718096' }}>{t.loadingReservations}</div>;
  }

  return (
    <div className="page-container" style={{ maxWidth: 1200 }}>
      <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: 20 }}>{t.pageTitle}</h1>

      {success && <div style={{ color: 'green', marginBottom: 12, padding: 12, background: '#f0fff4', borderRadius: 6 }}>{success}</div>}
      {error && <div style={{ color: 'red', marginBottom: 12, padding: 12, background: '#fff5f5', borderRadius: 6 }}>{error}</div>}

      {/* Where the nav badge leads. Deliberately a link and not the queue itself: guests hear
          nothing until the manager answers, so this has to be visible on landing — but a full
          list of requests is not what the dashboard is for. */}
      {pendingExtensions > 0 && (
        <Link
          to="/stay-extensions"
          style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
            padding: '13px 16px', borderRadius: 10, textDecoration: 'none',
            background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e',
          }}
        >
          <span style={{
            display: 'grid', placeItems: 'center', width: 30, height: 30, borderRadius: 99,
            background: '#dc2626', color: '#fff', fontSize: '0.85rem', fontWeight: 700, flexShrink: 0,
          }}>{pendingExtensions}</span>
          <span style={{ fontWeight: 600, fontSize: '0.92rem' }}>{tse.pendingTitle}</span>
          <span style={{ marginLeft: 'auto', fontSize: '1.1rem' }}>›</span>
        </Link>
      )}

      <DepositQueue />

      {ubyportStatus && !ubyportStatus.available && (
        <div style={{ marginBottom: 12, padding: '12px 16px', background: '#fffbeb', border: '1px solid #f6ad55', borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>⚠️</span>
          <div>
            <strong style={{ color: '#7b341e' }}>{t.ubyportUnavailableTitle}</strong>
            {' '}{t.ubyportUnavailableBody}
            {ubyportStatus.error && <span style={{ color: '#975a16', fontSize: '0.85rem', display: 'block', marginTop: 2 }}>{ubyportStatus.error}</span>}
          </div>
        </div>
      )}


      <div className="home-nav-panel" style={{ marginBottom: 20, padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        {isMobile ? (
          <>
            <div className="home-nav-row" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
              <button onClick={goToPrevious} className="home-nav-main-btn" style={{ padding: '8px 12px', background: '#3182ce', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span className="home-nav-btn-icon">{'<'}</span>
                <span className="home-nav-btn-label">{viewMode === '1day' ? t.prevDay : viewMode === '3day' ? t.prev3Days : t.prev5Days}</span>
              </button>
              <button onClick={goToToday} className="home-nav-main-btn" style={{ padding: '8px 12px', background: '#48bb78', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {t.today}
              </button>
              <button onClick={goToNext} className="home-nav-main-btn" style={{ padding: '8px 12px', background: '#3182ce', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span className="home-nav-btn-icon">{'>'}</span>
                <span className="home-nav-btn-label">{viewMode === '1day' ? t.nextDay : viewMode === '3day' ? t.next3Days : t.next5Days}</span>
              </button>
            </div>
            <div className="home-nav-row" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'nowrap', marginTop: 8 }}>
              <input type="date" value={formatDate(currentDate)} onChange={handleDateChange} style={{ padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, flex: 1, minWidth: 0 }} />
              <select id="home-view-mode" value={viewMode} onChange={(e) => setViewMode(e.target.value as ViewMode)} style={{ padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, background: '#fff', color: '#2d3748', fontWeight: 600 }}>
                <option value="1day">{t.viewDay}</option>
                <option value="3day">{t.view3Days}</option>
                <option value="5day">{t.view5Days}</option>
              </select>
              <select id="home-page-size" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, background: '#fff', color: '#2d3748', fontWeight: 600 }}>
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n} {t.perPage}</option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Left: nav buttons */}
            <button onClick={goToPrevious} style={{ padding: '8px 12px', background: '#3182ce', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {'<'} {viewMode === '1day' ? t.prevDayShort : viewMode === '3day' ? t.prev3DaysShort : t.prev5DaysShort}
            </button>
            <button onClick={goToToday} style={{ padding: '8px 12px', background: '#48bb78', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}>
              {t.today}
            </button>
            <button onClick={goToNext} style={{ padding: '8px 12px', background: '#3182ce', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {viewMode === '1day' ? t.nextDay : viewMode === '3day' ? t.next3Days : t.next5Days} {'>'}
            </button>
            {/* Center: date picker */}
            <input type="date" value={formatDate(currentDate)} onChange={handleDateChange} style={{ padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, flex: 1, minWidth: 0 }} />
            {/* Right: selectors */}
            <select id="home-view-mode" value={viewMode} onChange={(e) => setViewMode(e.target.value as ViewMode)} style={{ padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, background: '#fff', color: '#2d3748', fontWeight: 600, flexShrink: 0 }}>
              <option value="1day">{t.viewDay}</option>
              <option value="3day">{t.view3Days}</option>
              <option value="5day">{t.view5Days}</option>
            </select>
            <select id="home-page-size" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, background: '#fff', color: '#2d3748', fontWeight: 600, flexShrink: 0 }}>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n} {t.perPage}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 16, textAlign: 'center' }}>{getDateRangeDisplay()}</div>

      {/* Action bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {/* Left: primary actions */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={handleAdd} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#38a169', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}>
            <PlusIcon size={16} color="#fff" /> {t.addReservation}
          </button>
        </div>

        {/* Right: view toggles — segmented control */}
        <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', flexShrink: 0, margin: isMobile ? '0 auto' : undefined }}>
          <button
            type="button"
            onClick={() => setShowOngoing((v) => !v)}
            aria-pressed={showOngoing}
            style={{
              padding: '8px 14px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
              border: 'none', borderRight: '1px solid #e2e8f0',
              background: showOngoing ? '#2d3748' : '#fff',
              color: showOngoing ? '#fff' : '#718096',
            }}
          >
            {t.ongoing}
          </button>
          <button
            type="button"
            onClick={() => setShowIncoming((v) => !v)}
            aria-pressed={showIncoming}
            style={{
              padding: '8px 14px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
              border: 'none',
              background: showIncoming ? '#2d3748' : '#fff',
              color: showIncoming ? '#fff' : '#718096',
            }}
          >
            {t.incoming}
          </button>
        </div>
      </div>

      {showForm && (
        <div style={{ marginBottom: 24 }} className="form-card">
          <h3 style={{ marginTop: 0 }}>{t.newReservationTitle}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-field">
                <label className="form-label">{t.formApartment}</label>
                <select className="form-control" name="apartmentId" value={form.apartmentId} onChange={handleFormChange} required>
                  <option value="">{t.selectApartment}</option>
                  {apartments.map((apt) => (
                    <option key={apt.id} value={apt.id}>{apt.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label className="form-label">{t.formGuestName}</label>
                <input className="form-control" name="guestName" value={form.guestName} onChange={handleFormChange} required />
              </div>
              <div className="form-field">
                <label className="form-label">{t.formCheckInDate}</label>
                <input className="form-control" type="date" name="checkInDate" value={form.checkInDate} onChange={handleFormChange} required />
              </div>
              <div className="form-field">
                <label className="form-label">{t.formCheckOutDate}</label>
                <input className="form-control" type="date" name="checkOutDate" value={form.checkOutDate} onChange={handleFormChange} required />
              </div>
              <div className="form-field">
                <label className="form-label">{t.formNumberOfGuests}</label>
                <input className="form-control" type="number" name="numberOfGuests" min="1" value={form.numberOfGuests ?? ''} onChange={handleFormChange} required />
              </div>
              <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">{t.formNotes}</label>
                <textarea className="form-control" name="notes" value={form.notes || ''} onChange={handleFormChange} rows={3} />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-sm btn-primary">
                {tc.save}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn btn-sm btn-muted">
                {tc.cancel}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#718096' }}>{t.loadingReservations}</div>
      ) : reservations.length === 0 && dedupedAttentionReservations.length === 0 ? (
        apartments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ fontSize: '3rem', marginBottom: 16 }}>🏠</div>
            <h2 style={{ margin: '0 0 12px', fontSize: '1.4rem', color: '#1a202c' }}>{t.emptyNoApartmentsTitle}</h2>
            <p style={{ color: '#718096', marginBottom: 28, maxWidth: 460, margin: '0 auto 28px', lineHeight: 1.6 }}>{t.emptyNoApartmentsBody}</p>
            <Link
              to="/apartments"
              style={{ display: 'inline-flex', alignItems: 'center', background: '#38a169', color: '#fff', borderRadius: 8, padding: '12px 24px', fontWeight: 700, fontSize: '1rem', textDecoration: 'none' }}
            >
              {t.emptyNoApartmentsCta}
            </Link>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#718096', background: '#fff', borderRadius: 8 }}>
            <div>{viewMode === '1day' ? t.noReservationsDay : viewMode === '3day' ? t.noReservations3Day : t.noReservations5Day} {t.noReservationsAddHint}</div>
            <div style={{ marginTop: 16, fontSize: '0.88rem', color: '#a0aec0' }}>
              {t.calendarSyncHint}{' '}
              <Link
                to={apartments.length === 1 ? `/apartments/${apartments[0].id}?tab=calendar` : '/apartments'}
                style={{ color: '#3182ce', fontWeight: 600 }}
              >
                {t.calendarSyncLink}
              </Link>
            </div>
          </div>
        )
      ) : (
        <div className="home-reservations-container" style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 20 }}>
          <h3 style={{ marginTop: 0 }}>
            {t.reservationsTitle(reservations.filter(r => r.status !== 'CANCELLED').length)}
          </h3>
          <div style={{ marginBottom: 12, fontSize: '0.9rem', color: '#718096' }}>
            {t.showing(pagedReservations.length, reservations.length)}
            {totalPages > 1 && ` ${t.pageInfo(page + 1, totalPages)}`}
          </div>
          {reservations.length === 0 && (
            <div style={{ marginBottom: 16, padding: 12, borderRadius: 6, background: '#f7fafc', color: '#4a5568' }}>
              {viewMode === '1day' ? t.noReservationsDay : viewMode === '3day' ? t.noReservations3Day : t.noReservations5Day}
            </div>
          )}
          {showOngoing && renderReservationSection(t.ongoingReservations, ongoingReservations)}
          {showIncoming && renderReservationSection(t.incomingReservations, incomingReservations)}

          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
};

export default Home;

